import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Pebble';
const SITE_URL = 'https://app.usepebble.app';
const DEFAULT_DESCRIPTION = 'Pebble — AI-powered vendor management for event organizers. Discover events, buy tickets, and grow your vendor business.';
const DEFAULT_IMAGE = `${SITE_URL}/og-share-image.png`;

/**
 * SEOHead — drop-in dynamic <head> manager.
 *
 * Props:
 *   title          string  — page title (without "| Pebble" suffix, added automatically)
 *   description    string  — meta description (max ~160 chars)
 *   image          string  — absolute URL for og:image / twitter:image
 *   url            string  — canonical URL (defaults to current href)
 *   type           string  — og:type, default "website" (use "article" for events)
 *   noIndex        bool    — set true to add noindex (e.g. auth pages)
 *   structuredData object  — JSON-LD schema.org object (serialized automatically)
 */
const SEOHead = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noIndex = false,
  structuredData,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Vendor Management for Event Organizers`;
  const canonicalUrl = url || (typeof window !== 'undefined' ? window.location.href : SITE_URL);
  const safeDescription = description?.slice(0, 160) || DEFAULT_DESCRIPTION;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={safeDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={safeDescription} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
